import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { AttendingTaskRow } from '../../data/attendingTasks';
import {
  TASK_EMPTY_DISPLAY,
  taskStatusPillClass,
  timeRemainingClass,
  urgencyTextClass,
  type TaskStatusKey,
} from '../../data/taskStatus';
import {
  fetchAttendingColumnSuggestions,
  listAttendingTasks,
} from '../../services/attendingTasks';
import { duplicateTaskCase, transferTaskCase } from '../../services/tasks';
import { useContinueDraftCase } from '../../hooks/useContinueDraftCase';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { ATTENDING_TASKS_RETURN } from '../task-case/taskCaseDetailsReturnNavigation';
import { criminalWizardPath } from '../wizard/criminal/CriminalWizardShell';
import { setFlashToast } from '../../utils/flashToast';
import { notificationRowHighlightClass } from '../../utils/taskRowHighlight';

const ANIM_FRAMES = ['.', '..', '...'] as const;
const EMPTY = TASK_EMPTY_DISPLAY;
const ATTENDING_RETURN_TO = '/tasks/attending';

const VIEW_ONLY_STATUS_KEYS = new Set<TaskStatusKey>(['completed', 'published_archived', 'archived']);

function attendingRowKey(r: AttendingTaskRow): string {
  const caseId = r.caseRequestId ?? r.id;
  const taskId = r.userTaskId?.trim();
  return taskId ? `${caseId}:${taskId}` : caseId;
}

function isViewOnlyRow(r: AttendingTaskRow): boolean {
  return VIEW_ONLY_STATUS_KEYS.has(r.statusKey);
}

function isDraftRow(r: AttendingTaskRow): boolean {
  const k = (r.statusKey ?? '').trim().toLowerCase();
  if (k === 'draft') return true;
  const s = (r.status ?? '').trim().toLowerCase();
  return s === 'draft';
}

function isCriminalAdviceCategoryLabel(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return normalized.includes('criminal') && (normalized.includes('advice') || normalized.includes('advise'));
}

export default function AttendingTasksPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { canUpdate, taskCaseLink } = useTaskCaseDetailsNavigation({ returnState: ATTENDING_TASKS_RETURN });
  const { continueDraftId, handleContinueDraft } = useContinueDraftCase(ATTENDING_RETURN_TO);

  const [rows, setRows] = useState<AttendingTaskRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'ref', dir: 'asc' });

  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [transferId, setTransferId] = useState<string | null>(null);
  const [animFrame, setAnimFrame] = useState(0);
  const anyActionLoading = duplicateId !== null || transferId !== null || continueDraftId !== null;

  useEffect(() => {
    if (!anyActionLoading) return;
    const id = setInterval(() => setAnimFrame((f) => (f + 1) % ANIM_FRAMES.length), 400);
    return () => clearInterval(id);
  }, [anyActionLoading]);

  const dots = ANIM_FRAMES[animFrame];

  const fetchAbortRef = useRef<AbortController | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef<Record<string, string>>({});

  const fetchList = useCallback(
    (p: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc') => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      const { signal } = fetchAbortRef.current;
      setLoading(true);
      setError(null);

      void listAttendingTasks({
        page: p - 1,
        pageSize: size,
        columnFilters: filters,
        sortBy: sortKey,
        sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
        signal,
      })
        .then((result) => {
          if (signal.aborted) return;
          if (p > result.totalPages && result.totalPages > 0) {
            setPage(result.totalPages);
            return;
          }
          setRows(result.items);
          setTotalCount(result.total);
          setTotalPages(result.totalPages);
        })
        .catch(() => {
          if (signal.aborted) return;
          setError(t('tasks.attending.loadFailed'));
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [t],
  );

  const fetchRef = useRef(fetchList);
  useEffect(() => {
    fetchRef.current = fetchList;
  }, [fetchList]);

  useEffect(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir);
    return () => fetchAbortRef.current?.abort();
  }, [page, pageSize, sort.key, sort.dir]);

  const handleFiltersChange = useCallback((colFilters: Record<string, string>) => {
    filtersRef.current = colFilters;
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchRef.current(1, pageSize, colFilters, sort.key, sort.dir);
    }, 400);
  }, [pageSize, sort.key, sort.dir]);

  const handleSortChange = useCallback((key: string, dir: 'asc' | 'desc') => {
    setSort({ key, dir });
    setPage(1);
  }, []);

  const handleDuplicate = useCallback(
    (row: AttendingTaskRow) => {
      const id = row.caseRequestId ?? row.id;
      if (duplicateId) return;
      setDuplicateId(id);
      void duplicateTaskCase(id)
        .then((newId) => {
          if (!newId) {
            setFlashToast({ message: t('tasks.cases.duplicateFailed'), variant: 'error' });
            return;
          }
          setFlashToast({ message: t('tasks.cases.duplicateSuccess'), variant: 'success' });
          handleContinueDraft({
            id: newId,
            ref: newId,
            fileCategory: row.fileCategory !== EMPTY ? row.fileCategory : undefined,
          });
        })
        .finally(() => setDuplicateId(null));
    },
    [duplicateId, handleContinueDraft, t],
  );

  const handleTransfer = useCallback(
    (row: AttendingTaskRow) => {
      const id = row.caseRequestId ?? row.id;
      if (transferId) return;
      setTransferId(id);
      void transferTaskCase(id)
        .then((newId) => {
          if (!newId) {
            setFlashToast({ message: t('tasks.cases.transferFailed'), variant: 'error' });
            return;
          }
          setFlashToast({ message: t('tasks.cases.transferSuccess'), variant: 'success' });
          nav(criminalWizardPath(1, newId), {
            state: { returnTo: ATTENDING_RETURN_TO, resumeFlow: 'criminal' },
          });
        })
        .finally(() => setTransferId(null));
    },
    [transferId, t, nav],
  );

  const columns = useMemo<Column<AttendingTaskRow>[]>(
    () => [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        filterPattern: /^[a-zA-Z0-9/._ -]*$/,
        render: (r) => (
          <span className="rn">
            <Link
              {...taskCaseLink({
                caseRequestId: r.caseRequestId ?? r.id,
                userTaskId: r.userTaskId,
                viewMode: isViewOnlyRow(r) || !canUpdate,
              })}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {r.ref}
            </Link>
          </span>
        ),
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('tasks.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => (
          <span style={{ color: r.subFileCategory === EMPTY ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>
        ),
      },
      {
        key: 'agency',
        header: t('tasks.shared.submittedAgency'),
        getValue: (r) => r.agency,
        render: (r) => <span style={{ color: r.agency === EMPTY ? 'var(--t3)' : undefined }}>{r.agency}</span>,
      },
      {
        key: 'submittedAt',
        header: t('tasks.shared.submittedAt'),
        filterType: 'date',
        getValue: (r) => r.submittedAtIso,
        render: (r) => (
          <span style={{ color: r.submittedAt === EMPTY ? 'var(--t3)' : undefined }}>{r.submittedAt}</span>
        ),
      },
      {
        key: 'caseNumber',
        header: t('tasks.shared.caseNumber'),
        getValue: (r) => r.caseNumber,
        render: (r) => (
          <span style={{ color: r.caseNumber === EMPTY ? 'var(--t3)' : undefined }}>{r.caseNumber}</span>
        ),
      },
      {
        key: 'stage',
        header: t('tasks.shared.currentWorkflowStage'),
        getValue: (r) => r.stage,
        render: (r) => <span style={{ color: r.stage === EMPTY ? 'var(--t3)' : undefined }}>{r.stage}</span>,
      },
      {
        key: 'status',
        header: t('tasks.shared.taskStatus'),
        getValue: (r) => r.status,
        render: (r) =>
          r.status === EMPTY ? (
            <span className="t-dash">{EMPTY}</span>
          ) : (
            <span className={`pill ${taskStatusPillClass(r.statusKey)}`}>{r.status}</span>
          ),
      },
      {
        key: 'time',
        header: t('tasks.shared.timeRemaining'),
        getValue: (r) => r.timeLabel,
        render: (r) => (
          <span className={timeRemainingClass(r.isOverdue, r.hasTimeValue)}>{r.timeLabel}</span>
        ),
      },
      {
        key: 'urgency',
        header: t('tasks.shared.urgencyLevel'),
        getValue: (r) => r.urgencyLevel,
        render: (r) => <span className={urgencyTextClass(r.urgencyLevel)}>{r.urgencyLevel}</span>,
      },
      {
        key: 'officer',
        header: t('tasks.shared.assignedOfficer'),
        getValue: (r) => r.assignedOfficer,
        render: (r) => (
          <span style={{ color: r.assignedOfficer === EMPTY ? 'var(--t3)' : undefined }}>{r.assignedOfficer}</span>
        ),
      },
      {
        key: 'supervisor',
        header: t('tasks.shared.supervisor'),
        getValue: (r) => r.supervisor,
        render: (r) => (
          <span style={{ color: r.supervisor === EMPTY ? 'var(--t3)' : undefined }}>{r.supervisor}</span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => {
          const rowId = r.caseRequestId ?? r.id;
          const viewOnly = isViewOnlyRow(r) || !canUpdate;
          const draft = isDraftRow(r);
          const isDuplicating = duplicateId === rowId;
          const isTransferring = transferId === rowId;
          const canTransfer = !draft && isCriminalAdviceCategoryLabel(r.fileCategory);

          const detailsBtn = viewOnly ? (
            <Link
              {...taskCaseLink({
                caseRequestId: rowId,
                userTaskId: r.userTaskId,
                viewMode: true,
              })}
              className="act-b"
              data-tip={t('tasks.shared.viewTaskCaseDetails')}
              aria-label={t('tasks.shared.viewTaskCaseDetails')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </Link>
          ) : (
            <Link
              {...taskCaseLink({
                caseRequestId: rowId,
                userTaskId: r.userTaskId,
                viewMode: false,
              })}
              className="act-b"
              data-tip={t('tasks.shared.updateTaskCaseDetails')}
              aria-label={t('tasks.shared.updateTaskCaseDetails')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
          );

          const duplicateBtn = !draft ? (
            <button
              className="act-b"
              type="button"
              data-tip={isDuplicating ? `${t('tasks.shared.duplicating')}${dots}` : t('tasks.shared.duplicateCase')}
              aria-label={isDuplicating ? `${t('tasks.shared.duplicating')}${dots}` : t('tasks.shared.duplicateCase')}
              disabled={isDuplicating || duplicateId !== null}
              onClick={() => handleDuplicate(r)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          ) : null;

          const transferBtn = canTransfer ? (
            <button
              className="act-b"
              type="button"
              data-tip={isTransferring ? `${t('tasks.shared.transferring')}${dots}` : t('tasks.shared.transferCase')}
              aria-label={isTransferring ? `${t('tasks.shared.transferring')}${dots}` : t('tasks.shared.transferCase')}
              disabled={isTransferring || transferId !== null}
              onClick={() => handleTransfer(r)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M7 17 17 7" />
                <path d="M7 7h10v10" />
              </svg>
            </button>
          ) : null;

          return (
            <div className="act-wrap">
              {detailsBtn}
              {transferBtn}
              {duplicateBtn}
            </div>
          );
        },
      },
    ],
    [canUpdate, dots, duplicateId, handleDuplicate, handleTransfer, taskCaseLink, transferId, t],
  );

  return (
    <>
      <div className="bc">
        <span>{t('tasks.attending.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('tasks.attending.title')}</div>
        </div>
      </div>

      <div className="cr-table-wrap">
        {error ? <div className="si-err" style={{ margin: '12px 18px 0' }}>{error}</div> : null}

        <DataTable
          embedded
          title={t('tasks.attending.tableTitle')}
          columns={columns}
          rows={rows}
          rowKey={attendingRowKey}
          rowClassName={notificationRowHighlightClass}
          defaultRowsPerPage={5}
          rowsPerPageOptions={[5, 10, 15]}
          previousLabel={t('tasks.shared.previousBack')}
          nextLabel={t('tasks.shared.nextForward')}
          loading={loading}
          fetchColumnSuggestions={(col, prefix) => fetchAttendingColumnSuggestions(col, prefix)}
          onFiltersChange={handleFiltersChange}
          serverSort={{
            key: sort.key,
            dir: sort.dir,
            onSortChange: handleSortChange,
          }}
          serverPaging={{
            totalCount,
            totalPages,
            page,
            pageSize,
            onPageChange: (p) => setPage(p),
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(1);
            },
          }}
        />
      </div>
    </>
  );
}
